import RestartAltRounded from '@mui/icons-material/RestartAltRounded';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { errorMessage, useNotify } from '../../lib/notify';
import { useWhatsappAdmin, type WhatsappDocumentType, type WhatsappStatus } from './api';
import { PLACEHOLDERS } from './messages';

/** The documents WhatsApp can send, in the order the shop sees them. */
export const WHATSAPP_MODELS: { type: WhatsappDocumentType; label: string }[] = [
  { type: 'pos_order', label: 'POS receipts' },
  { type: 'quotation', label: 'Quotations' },
  { type: 'sales_order', label: 'Sales orders' },
  { type: 'sales_invoice', label: 'Invoices and Fast sale' },
  { type: 'delivery', label: 'Deliveries' },
  { type: 'sales_return', label: 'Sales returns' },
  { type: 'credit_note', label: 'Credit notes' },
  { type: 'purchase_order', label: 'Purchase orders' },
  { type: 'goods_receipt', label: 'Goods receipts' },
  { type: 'supplier_invoice', label: 'Supplier bills' },
  { type: 'purchase_return', label: 'Purchase returns' },
  { type: 'debit_note', label: 'Debit notes' },
  { type: 'payment', label: 'Payments' },
  { type: 'contact_ledger', label: 'Customer and supplier ledgers' },
];

type Plugin = WhatsappStatus['plugin'];

/**
 * Settings → WhatsApp → Messages: per kind of document, a switch (off hides its WhatsApp button)
 * and the message its button starts with. A saved message is used from the next send on.
 */
export function WhatsappMessages({ plugin, canManage }: { plugin: Plugin; canManage: boolean }) {
  return (
    <Card>
      <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
        <Typography variant="h6">Messages</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Turn WhatsApp on or off for each kind of document, and set the message its button starts
          with. You can use{' '}
          {PLACEHOLDERS.map((p, i) => (
            <span key={p.key}>
              {i > 0 && ', '}
              <Tooltip title={p.label}>
                <code>{`{${p.key}}`}</code>
              </Tooltip>
            </span>
          ))}
          .
        </Typography>
        <Stack spacing={0} sx={{ mt: 2 }}>
          {WHATSAPP_MODELS.map((m) => (
            <ModelRow
              key={m.type}
              type={m.type}
              label={m.label}
              plugin={plugin}
              canManage={canManage}
            />
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}

function ModelRow({
  type,
  label,
  plugin,
  canManage,
}: {
  type: WhatsappDocumentType;
  label: string;
  plugin: Plugin;
  canManage: boolean;
}) {
  const notify = useNotify();
  const admin = useWhatsappAdmin();
  const saved = plugin.templates[type];
  // The edit in progress; null shows what is saved.
  const [draft, setDraft] = useState<string | null>(null);
  const text = draft ?? saved;
  const on = !plugin.offTypes.includes(type);
  const custom = plugin.customized.includes(type);
  const busy = admin.settings.isPending;
  const save = (
    body: Parameters<typeof admin.settings.mutate>[0],
    done: string,
    clearDraft = false,
  ) =>
    admin.settings.mutate(body, {
      onSuccess: () => {
        if (clearDraft) setDraft(null);
        notify.success(done);
      },
      onError: (e) => notify.error(errorMessage(e)),
    });

  return (
    <Box sx={{ borderTop: 1, borderColor: 'divider', py: 2 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Switch
          checked={on}
          disabled={!canManage || busy}
          slotProps={{ input: { 'aria-label': `WhatsApp for ${label}` } }}
          onChange={(e) =>
            save(
              {
                offTypes: e.target.checked
                  ? plugin.offTypes.filter((t) => t !== type)
                  : [...plugin.offTypes, type],
                version: plugin.version ?? 1,
              },
              `WhatsApp ${e.target.checked ? 'on' : 'off'} for ${label.toLowerCase()}`,
            )
          }
        />
        <Typography sx={{ fontWeight: 600, flex: 1 }}>{label}</Typography>
        {custom && <Chip size="small" variant="outlined" label="Own message" />}
        {!on && <Chip size="small" variant="outlined" label="Off" />}
      </Stack>
      {on && (
        <Box sx={{ pl: { sm: 7 }, mt: 1 }}>
          <TextField
            value={text}
            onChange={(e) => setDraft(e.target.value)}
            multiline
            minRows={2}
            maxRows={8}
            fullWidth
            disabled={!canManage}
            slotProps={{ htmlInput: { maxLength: 1000, 'aria-label': `${label} message` } }}
          />
          {canManage && (
            <Stack direction="row" spacing={1} sx={{ mt: 1, justifyContent: 'flex-end' }}>
              {custom && (
                <Button
                  size="small"
                  startIcon={<RestartAltRounded />}
                  disabled={busy}
                  onClick={() =>
                    save(
                      { templates: { [type]: null }, version: plugin.version ?? 1 },
                      'Default message back',
                      true,
                    )
                  }
                >
                  Use default
                </Button>
              )}
              {text.trim() !== saved && (
                <>
                  <Button size="small" disabled={busy} onClick={() => setDraft(null)}>
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={busy || !text.trim()}
                    onClick={() =>
                      save(
                        { templates: { [type]: text.trim() }, version: plugin.version ?? 1 },
                        'Message saved',
                        true,
                      )
                    }
                  >
                    Save
                  </Button>
                </>
              )}
            </Stack>
          )}
        </Box>
      )}
    </Box>
  );
}
